import log from "logbeacon";
import type { MutableRefObject } from "react";
import type { DeepLinkOpenedData } from "./types";
import { applyDeepLinkPayload, extractResultId } from "./deepLinkSession";
import { mapDeepLinkToRoute } from "./deepLinkRouting";
import { captureAdSourceFromDeepLink } from "@/src/utils/onboarding";

type DeepLinkRouter = {
  push: (route: string) => void;
};

/**
 * 处理 deep-link-opened / get-last-deep-link payload。
 * resultId 场景只解析持久化，不跳转；其余走 mapDeepLinkToRoute + router.push。
 */
export function handleDeepLinkOpened(
  data: DeepLinkOpenedData,
  via: "push" | "pull",
  options: {
    router: DeepLinkRouter;
    lastHandledDeepLinkKeyRef: MutableRefObject<string | null>;
  },
): void {
  const { router, lastHandledDeepLinkKeyRef } = options;
  const dedupeKey = data?.url?.trim() || extractResultId(data) || "";
  if (dedupeKey && lastHandledDeepLinkKeyRef.current === dedupeKey) {
    log.debug("[DEEPLINK] skip duplicate payload", { dedupeKey, via });
    return;
  }

  log.debug("[DEEPLINK] payload received", {
    via,
    source: data?.source,
    platform: data?.platform,
    originalUrl: data?.originalUrl,
    finalUrl: data?.url,
    businessQuery: data?.businessQuery,
  });

  // 广告来源捕获：businessQuery.ad_source → cookie（后端登录后可覆盖）
  const capturedAdSource = captureAdSourceFromDeepLink(data);
  if (capturedAdSource) {
    log.debug("[DEEPLINK] ad_source captured to cookie", {
      adSource: capturedAdSource,
      via,
    });
  }

  const { resultId } = applyDeepLinkPayload(data);

  if (resultId) {
    log.info("[DEEPLINK] resultId parsed (no navigation)", {
      resultId,
      originalUrl: data?.originalUrl,
      finalUrl: data?.url,
      source: data?.source,
      platform: data?.platform,
      via,
    });
    // resultId 已统一写入 onboarding 业务态存储；此处只负责 bridge 入站解析，不再跳转。
    if (dedupeKey) {
      lastHandledDeepLinkKeyRef.current = dedupeKey;
    }
    return;
  }

  let route: string | undefined;
  try {
    route = mapDeepLinkToRoute(data?.url ?? "");
  } catch (error) {
    log.warn("[DEEPLINK] invalid deep link url from native", {
      rawUrl: data?.url,
      error,
    });
  }
  if (!route) {
    log.warn("[DEEPLINK] route mapping failed", { data, via });
    return;
  }

  log.info("[DEEPLINK] route mapped", {
    originalUrl: data?.originalUrl,
    finalUrl: data?.url,
    route,
    source: data?.source,
    platform: data?.platform,
    via,
  });
  if (dedupeKey) {
    lastHandledDeepLinkKeyRef.current = dedupeKey;
  }
  router.push(route);
}
